import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import styles from './Workspace.module.scss';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const STEPS = [0.25, 0.5, 0.67, 0.75, 0.9, 1, 1.25, 1.5, 1.75, 2];

// Presentation state only: zoom never changes the canvas document or its history.
export const usePreviewZoom = (viewportRef, device, expanded) => {
  const contentRef = useRef(null);
  const [manualScale, setManualScale] = useState(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const width = device === 'mobile' ? 390 : 1120;
  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return undefined;
    const measure = () => {
      const css = window.getComputedStyle(viewport);
      setAvailableWidth(Math.max(0, viewport.clientWidth - (parseFloat(css.paddingLeft) || 0) - (parseFloat(css.paddingRight) || 0)));
      setContentHeight(content.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) { observer.observe(viewport); observer.observe(content); }
    return () => { if (observer) observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [viewportRef, device, expanded]);
  const fitScale = availableWidth ? Math.min(1, Math.max(MIN_ZOOM, availableWidth / width)) : 1;
  const scale = manualScale === null ? fitScale : manualScale;
  const change = direction => setManualScale(direction > 0
    ? STEPS.find(step => step > scale + 0.001) || MAX_ZOOM
    : [...STEPS].reverse().find(step => step < scale - 0.001) || MIN_ZOOM);
  const reset = () => setManualScale(1);
  const fit = () => setManualScale(null);
  const onKeyDown = event => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey
      || event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="spinbutton"], [role="slider"], [role="combobox"]')) return;
    const key = event.key.toLowerCase();
    if (!['+', '=', '-', '0', 'f'].includes(key)) return;
    event.preventDefault();
    event.stopPropagation();
    if (key === '0') reset();
    else if (key === 'f') fit();
    else change(key === '-' ? -1 : 1);
  };
  return { contentRef, width, contentHeight, scale, change, reset, fit, onKeyDown,
    fitting: manualScale === null, canZoomOut: scale > MIN_ZOOM + 0.001, canZoomIn: scale < MAX_ZOOM - 0.001 };
};

export const PreviewZoomControls = ({ zoom }) => {
  const t = useLocalizedMessage();
  const label = name => t(`searchInterface.preview.zoom.${name}`);
  return <div className={styles.zoomControls} role="group" aria-label={label('label')} data-testid="preview-zoom-controls">
    <Tooltip title={`${label('out')} (−)`}><span><IconButton size="small" disabled={!zoom.canZoomOut} onClick={() => zoom.change(-1)} aria-label={label('out')} aria-keyshortcuts="-"><RemoveIcon fontSize="small" /></IconButton></span></Tooltip>
    <Tooltip title={`${label('reset')} (0)`}><Button size="small" onClick={zoom.reset} aria-label={label('reset')} aria-keyshortcuts="0" data-testid="preview-zoom-reset"><span aria-live="polite">{Math.round(zoom.scale * 100)}%</span></Button></Tooltip>
    <Tooltip title={`${label('in')} (+)`}><span><IconButton size="small" disabled={!zoom.canZoomIn} onClick={() => zoom.change(1)} aria-label={label('in')} aria-keyshortcuts="+ ="><AddIcon fontSize="small" /></IconButton></span></Tooltip>
    <Tooltip title={label('fit')}><Button size="small" onClick={zoom.fit} aria-label={label('fit')} aria-pressed={zoom.fitting} aria-keyshortcuts="f">{label('fit')}</Button></Tooltip>
  </div>;
};
